import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
  Chip,
} from "@mui/material";
import { Search, Add, Close } from "@mui/icons-material";
import { getImageUrl } from "../../../utils/imageHelper";
import { useSnackbar } from "notistack";
import SongDetailModal from "./SongDetailModal";

const BATCH_SIZE = 50;

const SearchTab = ({ onAddToQueue }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [query, setQuery] = useState("");
  const [allSongs, setAllSongs] = useState([]);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const debounceRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const sentinelRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const fetchAllSongs = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/songs`);
      const data = await res.json();
      setAllSongs(data);
      setFilteredSongs(data);
      setVisibleCount(BATCH_SIZE);
    } catch (err) {
      console.error("Error fetching songs:", err);
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllSongs();
  }, [fetchAllSongs]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const sentinel = sentinelRef.current;
    if (!container || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => prev + BATCH_SIZE);
        }
      },
      { root: container, rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [filteredSongs]);

  const handleSearch = useCallback(
    (searchQuery) => {
      setVisibleCount(BATCH_SIZE);
      if (!searchQuery.trim()) {
        setFilteredSongs(allSongs);
        return;
      }
      setLoading(true);
      const q = searchQuery.toLowerCase();
      const filtered = allSongs.filter(
        (s) =>
          s.name?.toLowerCase().includes(q) ||
          s.artist?.toLowerCase().includes(q)
      );
      setFilteredSongs(filtered);
      setLoading(false);
    },
    [allSongs]
  );

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(val), 250);
  };

  const handleAdd = async (e, song) => {
    e.stopPropagation();
    try {
      await onAddToQueue(song.songs_master_id);
      enqueueSnackbar(`"${song.name}" added to queue`, {
        variant: "success",
        autoHideDuration: 1500,
      });
    } catch {
      enqueueSnackbar("Failed to add to queue", { variant: "error" });
    }
  };

  const handleSongClick = (song) => {
    setSelectedSong(song);
    setModalOpen(true);
  };

  const visibleSongs = filteredSongs.slice(0, visibleCount);
  const totalCount = allSongs.length;
  const matchCount = filteredSongs.length;
  const showingCount = visibleSongs.length;

  if (initialLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={32} sx={{ color: "#1DB954" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ p: 2, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search songs or artists..."
          value={query}
          onChange={handleQueryChange}
          autoComplete="off"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "rgba(255,255,255,0.06)",
              borderRadius: 2,
              fontSize: "0.875rem",
              "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
              "&:hover fieldset": { borderColor: "rgba(255,255,255,0.2)" },
              "&.Mui-focused fieldset": { borderColor: "#1DB954" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "rgba(255,255,255,0.4)", fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setQuery("");
                    handleSearch("");
                  }}
                >
                  <Close sx={{ fontSize: 16, color: "rgba(255,255,255,0.4)" }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: 1,
          }}
        >
          <Typography
            sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.35)" }}
          >
            {query
              ? `Showing ${showingCount} of ${matchCount} matches`
              : `Showing ${showingCount} of ${totalCount} songs`}
          </Typography>
          <Chip
            label={`Total: ${totalCount}`}
            size="small"
            sx={{
              height: 18,
              fontSize: "0.6rem",
              backgroundColor: "rgba(29,185,84,0.12)",
              color: "#1DB954",
            }}
          />
        </Box>
      </Box>

      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255,255,255,0.1)",
            borderRadius: 2,
          },
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} sx={{ color: "#1DB954" }} />
          </Box>
        ) : filteredSongs.length === 0 ? (
          <Typography
            sx={{
              textAlign: "center",
              py: 4,
              color: "rgba(255,255,255,0.4)",
              fontSize: "0.85rem",
            }}
          >
            No songs found
          </Typography>
        ) : (
          <>
            {visibleSongs.map((song) => (
              <Box
                key={song.songs_master_id}
                onClick={() => handleSongClick(song)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1,
                  borderBottom: "1px solid rgba(255,255,255,0.04)",
                  cursor: "pointer",
                  transition: "background-color 0.15s",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
                }}
              >
                <Box
                  component="img"
                  src={getImageUrl(song.image)}
                  alt={song.name}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 1,
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {song.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "rgba(255,255,255,0.5)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {song.artist || "Unknown Artist"}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => handleAdd(e, song)}
                  sx={{
                    color: "#1DB954",
                    flexShrink: 0,
                    "&:hover": { backgroundColor: "rgba(29,185,84,0.1)" },
                  }}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Box>
            ))}
            {visibleCount < filteredSongs.length && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={20} sx={{ color: "#1DB954" }} />
              </Box>
            )}
            <div ref={sentinelRef} style={{ height: 1 }} />
          </>
        )}
      </Box>

      <SongDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        songId={selectedSong?.songs_master_id}
        songData={selectedSong}
      />
    </Box>
  );
};

export default SearchTab;
